import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Pagination, CircularProgress, Chip, TableSortLabel, TextField, InputAdornment,
    IconButton, Menu, MenuItem, Button, Checkbox,
    Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Tooltip,
    Autocomplete, Select, FormControl, InputLabel, Popover, ListItemText, Snackbar, Alert, Grid,
    ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import dayjs from 'dayjs';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import { fetchDispatchReservations, saveDispatchReservation, updateVehicle } from '../api/dispatchService';
import { useReservationData } from '../context/ReservationDataContext';

import { useReservationStatuses } from '../hooks/useReservationStatuses';
import { getStatusColor as _getStatusColor, getStatusLabel as _getStatusLabel, buildStatusToLabel } from '../utils/statusUtils';
import { useSquarePaymentData } from '../hooks/useSquarePaymentData';
import { computePaymentStatus } from '../utils/paymentStatus';
import { useReservationFilters } from '../hooks/useReservationFilters';

// Square Invoicesの対象ステータス（予約確定=1, 貸出済み=2, 貸出中=3, 要連絡/未確定=6）
const SQUARE_TARGET_STATUSES = [1, 2, 3, 6];

// ... 日付フォーマット等の関数 ...

// 日付フォーマット
const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    try {
        const date = new Date(dateStr);
        if (Number.isNaN(date.getTime())) return '-';
        return `${date.getMonth() + 1}/${date.getDate()} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    } catch {
        return '-';
    }
};

// 日付期間フォーマット
const formatDateRange = (startDate: any, endDate: any): string => {
    const start = formatDate(startDate);
    const end = formatDate(endDate);
    if (start === '-' && end === '-') return '-';
    return `${start} ～ ${end}`;
};

// 金額フォーマット
const formatPrice = (price: any): string => {
    if (price == null || price === '') return '-';
    const num = Number(price);
    if (Number.isNaN(num)) return '-';
    return `¥${num.toLocaleString()}`;
};

// ソート用の比較関数
const comparator = (a: any, b: any, orderBy: string, order: string): number => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];

    // contact_media は detail 配下にあるため、top-level がなければフォールバック
    if (orderBy === 'contact_media') {
        aVal = a.contact_media ?? a.detail?.contact_media;
        bVal = b.contact_media ?? b.detail?.contact_media;
    }

    // null/undefined を処理
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';

    // 日付の場合は Date オブジェクトで比較
    if (orderBy === 'booking_date' || orderBy === 'start_date') {
        aVal = new Date(aVal).getTime() || 0;
        bVal = new Date(bVal).getTime() || 0;
    }

    // 数値の場合
    if (orderBy === 'id' || orderBy === 'sum_price' || orderBy === 'payment') {
        if (orderBy === 'payment') {
            aVal = Number(a.detail?.payment) || 0;
            bVal = Number(b.detail?.payment) || 0;
        } else {
            aVal = Number(aVal) || 0;
            bVal = Number(bVal) || 0;
        }
    }

    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
};

import { CAR_TYPES, ensureCarTypesLoaded, resolveCarTypeColor, resolveCarTypeTextColor } from '../data/carTypes';
import ReservationEditorPanel from '../components/editor2';
import { toEditorReservation } from '../lib/editorDataTransform';
import { reservationSyncService } from '../services/reservationSyncService';
import VehicleAssignModal from '../components/modals/VehicleAssignModal';

const ITEMS_PER_PAGE = 100;

export default function ReservationList(): React.ReactElement {
    const { reservationStatusList, vehicleStatusList } = useReservationStatuses();

    // ステータスID/コード→ラベルのマッピングを生成
    const statusToLabel = useMemo(() => buildStatusToLabel(reservationStatusList), [reservationStatusList]);

    // ステータスカラーを取得
    const getStatusColor = useCallback((statusOrRes) => _getStatusColor(statusOrRes, reservationStatusList), [reservationStatusList]);

    // ステータスラベルを取得
    const getStatusLabel = useCallback((status) => _getStatusLabel(status, statusToLabel), [statusToLabel]);

    const { reservations: contextReservations, vehicles: contextVehicles, isLoadingReservations } = useReservationData();

    const [reservations, setReservations] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [orderBy, setOrderBy] = useState<string>('booking_date');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [selectedReservation, setSelectedReservation] = useState<any>(null);
    const [editingReservation, setEditingReservation] = useState<any>(null);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // 車両アサインモーダル用state
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [assignTargetReservation, setAssignTargetReservation] = useState<any>(null);

    // フィルタ & 重複検索用state
    const [carTypesList, setCarTypesList] = useState<any[]>([]);
    const [duplicatesModalOpen, setDuplicatesModalOpen] = useState<boolean>(false);
    const [duplicateReservations, setDuplicateReservations] = useState<any[]>([]);
    const [duplicateSearchLoading, setDuplicateSearchLoading] = useState(false);
    const [resStatusModal, setResStatusModal] = useState<{ open: boolean; reservation: any }>({ open: false, reservation: null });
    const [refreshMenuAnchor, setRefreshMenuAnchor] = useState<HTMLElement | null>(null);

    // Square Invoices（フック経由）
    const { squarePaymentCache, squarePaymentLoading, fetchPaymentData, getPaymentData, refreshSingleContact } = useSquarePaymentData(SQUARE_TARGET_STATUSES);

    // Square支払確認モーダル state
    const [squareModal, setSquareModal] = useState<{
        open: boolean;
        reservation: any;
        paymentData: any;
        loading: boolean;
    }>({
        open: false,
        reservation: null,
        paymentData: null,
        loading: false,
    });

    // vehicle_id → vehicle_code マッピング
    const vehicleIdToCode = useMemo(() => {
        const map = {};
        Object.values(vehicles).forEach(v => {
            if (v.id != null && v.vehicle_code) {
                map[String(v.id)] = v.vehicle_code;
            }
        });
        return map;
    }, [vehicles]);

    // フィルタ・検索・ページネーション（フック経由）
    const {
        filterCarType, setFilterCarType,
        filterStatus, setFilterStatus,
        filterVehicle, setFilterVehicle,
        filterNoVehicle,
        filterInvoice,
        searchQuery,
        displayDateStart, setDisplayDateStart,
        displayDateEnd, setDisplayDateEnd,
        dateFilterTarget, setDateFilterTarget,
        dateRangeAnchor, setDateRangeAnchor,
        page, setPage,
        filteredReservations,
        handleResetFilters,
        handleSearchChange,
        handleClearSearch,
        handleFilterNoVehicleToggle,
        handleFilterInvoiceToggle,
        handlePageChange,
    } = useReservationFilters({ reservations, vehicleIdToCode, reservationStatusList, carTypesList });

    const dateRangeButtonRef = useRef<HTMLButtonElement>(null);

    // Context から vehicles を反映
    useEffect(() => {
        if (Object.keys(contextVehicles).length > 0) {
            setVehicles(contextVehicles);
        }
    }, [contextVehicles]);

    // fetchDataをオーバーライドしてSquare取得を追加
    const fetchDataWithSquare = useCallback(async (start: string, end: string, silent: boolean = false) => {
        if (!silent) setLoading(true);
        setError(null);
        try {
            const data = await fetchDispatchReservations({ start_date: start, end_date: end });

            const newReservations = Array.isArray(data) ? data : [];

            // 取得したデータに車両コードを付与
            newReservations.forEach(reservation => {
                if (reservation.vehicle_id && vehicles[String(reservation.vehicle_id)]) {
                    reservation.vehicle_code = vehicles[String(reservation.vehicle_id)].vehicle_code;
                }
            });

            // 既存の予約リストとマージ（Upsert）
            setReservations(prev => {
                const map = new Map();
                (Array.isArray(prev) ? prev : []).forEach(r => {
                    if (r && r.id) map.set(String(r.id), r);
                });
                newReservations.forEach(r => {
                    if (r && r.id) map.set(String(r.id), r);
                });
                return Array.from(map.values());
            });

            // Squareデータを非同期で取得
            fetchPaymentData(newReservations);

        } catch (err) {
            console.error('Failed to fetch reservations:', err);
            setError('データの取得に失敗しました');
        } finally {
            if (!silent) setLoading(false);
        }
    }, [fetchPaymentData]);

    const fetchData = fetchDataWithSquare;

    // 支払確認ボタンクリックハンドラ（DS1: search_by_contact.php）
    const handlePaymentCheck = useCallback(async (reservation: any) => {
        const contactCode = reservation.contact_code || reservation.detail?.contact_id;
        if (!contactCode) {
            setSnackbarMessage('予約番号が見つかりません');
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
            return;
        }

        // キャッシュからデータを取得
        const cached = getPaymentData(contactCode);
        setSquareModal({
            open: true,
            reservation,
            paymentData: cached || null,
            loading: !cached,
        });

        // キャッシュがない場合は DS1 から取得
        if (!cached) {
            try {
                await refreshSingleContact(contactCode);
            } catch (error) {
                console.error('Failed to fetch payment data on demand:', error);
            }
        }
    }, [getPaymentData, refreshSingleContact]);

    // squarePaymentCache が更新されたらモーダルのデータも同期
    useEffect(() => {
        if (!squareModal.open || !squareModal.reservation) return;
        const contactCode = squareModal.reservation.contact_code || squareModal.reservation.detail?.contact_id;
        if (!contactCode) return;
        const entry = squarePaymentCache[contactCode];
        if (entry) {
            setSquareModal(prev => ({ ...prev, paymentData: entry, loading: false }));
        }
    }, [squarePaymentCache, squareModal.open, squareModal.reservation]);

    // 車種ロード
    useEffect(() => {
        ensureCarTypesLoaded().then(() => {
            setCarTypesList([...CAR_TYPES.list]);
        });
    }, []);

    // Context の予約データをローカル state に同期（Pusher 更新も即時反映）
    useEffect(() => {
        if (contextReservations.length > 0) {
            setReservations(prev => {
                // prev（ローカル）を先に入れ、context（最新）で上書き
                const map = new Map();
                (Array.isArray(prev) ? prev : []).forEach(r => { if (r?.id) map.set(String(r.id), r); });
                contextReservations.forEach(r => { if (r?.id) map.set(String(r.id), r); });
                return Array.from(map.values());
            });
            setLoading(false);
            fetchPaymentData(contextReservations);
        } else if (!isLoadingReservations) {
            setLoading(false);
        }
    }, [contextReservations, isLoadingReservations]); // fetchPaymentData is stable (useCallback)

    // データ更新: 将来データ
    const handleRefreshFuture = useCallback(() => {
        const start = dayjs().subtract(3, 'days').format('YYYY-MM-DD');
        const end = dayjs().add(1, 'year').format('YYYY-MM-DD');
        fetchData(start, end);
        setRefreshMenuAnchor(null);
    }, [fetchData]);

    // データ更新: 期間指定データ
    const handleRefreshPeriod = useCallback(() => {
        if (!displayDateStart || !displayDateEnd) {
            alert('期間が指定されていません。カレンダーアイコンから期間を選択してください。');
            setRefreshMenuAnchor(null);
            return;
        }
        fetchData(displayDateStart, displayDateEnd);
        setRefreshMenuAnchor(null);
    }, [fetchData, displayDateStart, displayDateEnd]);

    // 車両リスト（配列形式）- out_of_useを除外
    const vehicleList = useMemo(() => {
        return Object.values(vehicles).filter(v => v.id != null && !v.out_of_use);
    }, [vehicles]);

    // 車両アサインモーダルを開く
    const handleOpenAssignModal = useCallback((reservation: any) => {
        setAssignTargetReservation(reservation);
        setAssignModalOpen(true);
    }, []);

    // 車両アサインモーダルを閉じる
    const handleCloseAssignModal = useCallback(() => {
        setAssignModalOpen(false);
        setAssignTargetReservation(null);
    }, []);

    // 車両アサイン実行
    const executeAssignment = useCallback(async (reservation: any, vehicleId: any) => {
        try {
            // アサインする車両情報を取得
            const assignedVehicle = vehicles[String(vehicleId)];
            const updatePayload = {
                ...reservation,
                vehicle_id: vehicleId,
            };

            // 車両のアサイン時に車種を変更しないようにするため、
            // updatePayload.car_type の自動更新を削除しました。

            const savedReservation = await saveDispatchReservation(updatePayload);

            // リストを更新
            const updatedReservations = reservations.map(r => {
                if (String(r.id) === String(reservation.id)) {
                    // APIからのレスポンスを使用し、vehicle_codeを付与
                    const updated = { ...savedReservation };
                    if (updated.vehicle_id && vehicles[String(updated.vehicle_id)]) {
                        updated.vehicle_code = vehicles[String(updated.vehicle_id)].vehicle_code;
                    }
                    return updated;
                }
                return r;
            });
            setReservations(updatedReservations);
            handleCloseAssignModal();
            setSnackbarMessage('車両をアサインしました');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (err) {
            console.error('Failed to assign vehicle:', err);
            setSnackbarMessage('車両のアサインに失敗しました');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    }, [reservations, vehicles, handleCloseAssignModal]);

    // 予約ステータス変更ハンドラ
    const handleResStatusUpdate = useCallback((newStatusId: any) => {
        const reservation = resStatusModal.reservation;
        if (reservation) {
            setResStatusModal({ open: false, reservation: null });
            // パネルを開く。まずは元の予約データをセットして初期化させる
            setSelectedReservation(reservation);

            // ステータスの変更を非同期（次のTick）で行うことで、
            // エディタ側が「初期状態からの変更」として検知（isDirtyがtrue）できるようにする
            setTimeout(() => {
                setEditingReservation(prev => {
                    if (!prev) return prev;
                    return { ...prev, status: newStatusId };
                });
            }, 0);
        }
    }, [resStatusModal]);



    // 車両ステータスを更新
    const handleUpdateVehicleStatus = useCallback(async (vehicleId: any, statusId: any) => {
        if (!vehicleId) return;
        try {
            const statusInfo = vehicleStatusList.find(s => s.id === statusId);
            await updateVehicle({
                id: vehicleId,
                status: statusId,
            });
            // ローカル状態を更新
            setVehicles(prev => ({
                ...prev,
                [String(vehicleId)]: {
                    ...prev[String(vehicleId)],
                    status: statusId,
                    status_name: statusInfo?.label || '',
                },
            }));
        } catch (err) {
            console.error('Failed to update vehicle status:', err);
            alert('ステータスの更新に失敗しました');
        }
    }, [updateVehicle, vehicleStatusList]);




    // 予約選択時にエディタ用データを設定
    // アニメーションはReservationEditor内部で制御（ID変化を検知して閉じ→開き）
    useEffect(() => {
        if (selectedReservation) {
            setEditingReservation(toEditorReservation(selectedReservation));
        } else {
            setEditingReservation(null);
        }
    }, [selectedReservation]);

    const handleCopyReservation = (row: any) => {
        const currentCode = row.contact_code || '';
        const baseCode = currentCode.replace(/-(\d+)$/, '');

        // 既存の取得可能な全データから最大のスフィックスを計算
        const cachedData = reservationSyncService.getReservations();
        const allKnownReservations = [...(Array.isArray(reservations) ? reservations : []), ...cachedData];

        let maxSuffix = 1;
        if (baseCode) {
            const pattern = new RegExp(`^${baseCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:-(\\d+))?$`);
            allKnownReservations.forEach(r => {
                const code = r.contact_code;
                if (code) {
                    const match = code.match(pattern);
                    if (match) {
                        const suffix = match[1] ? parseInt(match[1], 10) : 1;
                        if (suffix > maxSuffix) maxSuffix = suffix;
                    }
                }
            });
        }

        const nextCode = baseCode ? `${baseCode}-${maxSuffix + 1}` : '';

        const copied = {
            ...row,
            id: null,
            contact_code: nextCode,
            __isNew: true,
            __isCopy: true
        };
        setSelectedReservation(copied);
    };

    // 指定期間の売上合計
    const totalSales = useMemo(() => {
        return filteredReservations.reduce((sum, res) => {
            const price = Number(res.detail?.sum_price) || 0;
            return sum + price;
        }, 0);
    }, [filteredReservations]);

    // 重複予約検索ハンドラ
    const handleFindDuplicates = useCallback(() => {
        setDuplicateSearchLoading(true);
        const conflicts = new Map(); // id -> reservation

        // キャンセル系ステータスを除外 (cancelled, nonreserved)
        // 数値IDと文字列コードの両方を考慮して除外リストを作成
        const excludeStatusKeys = ['cancelled', 'nonreserved', 'full'];
        const excludeSet = new Set();
        reservationStatusList.forEach(opt => {
            if (excludeStatusKeys.includes(opt.code)) {
                excludeSet.add(opt.code);          // "cancelled"
                excludeSet.add(String(opt.id));    // "4"
                excludeSet.add(opt.id);            // 4
            }
        });

        // statusが文字列表現("cancelled")でも数値(4)でも除外できるようにする
        const isActive = (r) => {
            if (r.status == null) return true;
            return !excludeSet.has(r.status) && !excludeSet.has(String(r.status)) && !excludeSet.has(Number(r.status));
        };

        const activeList = reservations.filter(isActive);

        for (let i = 0; i < activeList.length; i++) {
            const r1 = activeList[i];
            const v1 = r1.vehicle_id ?? r1.vehicleId;
            if (!v1) continue;

            const s1 = new Date(r1.start_date).getTime();
            const e1 = new Date(r1.end_date).getTime();
            if (isNaN(s1) || isNaN(e1)) continue;

            for (let j = i + 1; j < activeList.length; j++) {
                const r2 = activeList[j];
                const v2 = r2.vehicle_id ?? r2.vehicleId;

                // 車両ID比較
                if (!v2 || String(v1) !== String(v2)) continue;

                const s2 = new Date(r2.start_date).getTime();
                const e2 = new Date(r2.end_date).getTime();
                if (isNaN(s2) || isNaN(e2)) continue;

                // 期間重複チェック (Start1 < End2 AND End1 > Start2)
                if (s1 < e2 && e1 > s2) {
                    conflicts.set(r1.id, r1);
                    conflicts.set(r2.id, r2);
                }
            }
        }

        setDuplicateReservations(Array.from(conflicts.values()));
        setDuplicatesModalOpen(true);
        setDuplicateSearchLoading(false);
    }, [reservations]);

    // ソート済みデータ
    const sortedReservations = useMemo(() => {
        return [...filteredReservations].sort((a, b) => comparator(a, b, orderBy, order));
    }, [filteredReservations, orderBy, order]);

    // ページネーション
    const totalPages = Math.ceil(sortedReservations.length / ITEMS_PER_PAGE);
    const paginatedReservations = useMemo(() => {
        const start = (page - 1) * ITEMS_PER_PAGE;
        return sortedReservations.slice(start, start + ITEMS_PER_PAGE);
    }, [sortedReservations, page]);

    // ソート切り替え
    const handleSort = useCallback((property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setPage(1);
    }, [orderBy, order]);

    // CSVダウンロード
    const handleDownloadCSV = useCallback(() => {
        if (filteredReservations.length === 0) {
            alert('ダウンロードするデータがありません');
            return;
        }

        const headers = [
            'ID',
            '予約コード',
            'ステータス',
            '予約日',
            '顧客名',
            '電話番号',
            'メールアドレス',
            '車両',
            '車種',
            '貸出日',
            '返却日',
            '合計金額',
            '基本料金',
            'オプション料金',
            'NOC料金',
            'CDW料金',
            'プラン',
            '媒体',
            'メモ',
            'コメント'
        ];

        const rows = filteredReservations.map(res => {
            const vehicleId = res.vehicle_id ?? res.vehicleId;
            const vehicleCode = vehicleId != null ? vehicleIdToCode[String(vehicleId)] : '';
            const carType = res.car_type ?? res.re_car_type ?? res.req_car_type ?? '';
            const statusLabel = getStatusLabel(res.status);

            return [
                res.id,
                res.contact_code,
                statusLabel,
                res.booking_date ? dayjs(res.booking_date).format('YYYY-MM-DD HH:mm') : '',
                res.customer_name,
                res.customer_tel,
                res.customer_email,
                vehicleCode,
                carType,
                res.start_date ? dayjs(res.start_date).format('YYYY-MM-DD HH:mm') : '',
                res.end_date ? dayjs(res.end_date).format('YYYY-MM-DD HH:mm') : '',
                res.detail?.sum_price ?? 0,
                res.detail?.base_price ?? 0,
                res.detail?.option_price ?? 0,
                res.detail?.noc_price ?? 0,
                res.detail?.cdw_price ?? 0,
                res.detail?.contact_plan,
                res.detail?.contact_media,
                res.detail?.memo,
                res.detail?.comment
            ];
        });

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(val => {
                const s = String(val).replace(/"/g, '""');
                return s.includes(',') || s.includes('\n') || s.includes('"') ? `"${s}"` : s;
            }).join(','))
        ].join('\n');

        const fileName = `reservations_${dayjs().format('YYYYMMDD_HHmmss')}.csv`;
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [filteredReservations, vehicleIdToCode]);

    // ヘッダーセル
    const SortableHeaderCell = ({ id, label, width, align = 'left' }: { id: string; label: string; width: number; align?: string }) => (
        <TableCell
            sx={{
                fontWeight: 600,
                backgroundColor: '#f8fafc',
                width,
                whiteSpace: 'nowrap',
            }}
            align={align as any}
        >
            <TableSortLabel
                active={orderBy === id}
                direction={orderBy === id ? order : 'asc'}
                onClick={() => handleSort(id)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* ヘッダー */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        予約一覧
                    </Typography>
                    <Chip
                        label={`${filteredReservations.length} 件`}
                        size="small"
                        sx={{ fontWeight: 'bold', backgroundColor: '#e2e8f0' }}
                    />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: '#f1f5f9',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        border: '1px solid #cbd5e1'
                    }}>
                        <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            指定期間売上:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e293b' }}>
                            ¥{totalSales.toLocaleString()}
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={handleFindDuplicates}
                        disabled={duplicateSearchLoading}
                        startIcon={<WarningAmberIcon />}
                        size="small"
                    >
                        重複予約検索
                    </Button>
                    <Button
                        ref={dateRangeButtonRef}
                        variant="contained"
                        size="small"
                        startIcon={<CalendarMonthIcon />}
                        onClick={() => setDateRangeAnchor(dateRangeButtonRef.current)}
                        disableElevation
                    >
                        期間指定({dateFilterTarget === 'booking' ? '予約' : '貸出'}): {displayDateStart && displayDateEnd ? `${dayjs(displayDateStart).format('YY/MM/DD')} 〜 ${dayjs(displayDateEnd).format('YY/MM/DD')}` : '指定なし'}
                    </Button>
                    <Popover
                        open={Boolean(dateRangeAnchor)}
                        anchorEl={() => dateRangeButtonRef.current}
                        onClose={() => setDateRangeAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 250 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                期間で絞り込み (開始日基準)
                            </Typography>
                            <ToggleButtonGroup
                                value={dateFilterTarget}
                                exclusive
                                onChange={(e, val) => {
                                    if (val) {
                                        setDateFilterTarget(val);
                                        setPage(1);
                                    }
                                }}
                                size="small"
                                color="primary"
                                fullWidth
                            >
                                <ToggleButton value="rental" sx={{ fontSize: '0.75rem', py: 0.5 }}>貸出日</ToggleButton>
                                <ToggleButton value="booking" sx={{ fontSize: '0.75rem', py: 0.5 }}>予約日</ToggleButton>
                            </ToggleButtonGroup>
                            <TextField
                                label="開始日"
                                type="date"
                                value={displayDateStart}
                                onChange={(e) => {
                                    setDisplayDateStart(e.target.value);
                                    setPage(1);
                                }}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                            <TextField
                                label="終了日"
                                type="date"
                                value={displayDateEnd}
                                onChange={(e) => {
                                    setDisplayDateEnd(e.target.value);
                                    setPage(1);
                                }}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />

                            {/* クイックナビゲーションボタン */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    sx={{ flex: 1, fontSize: '0.75rem', py: 0.5 }}
                                    onClick={() => {
                                        const base = displayDateStart ? dayjs(displayDateStart) : dayjs();
                                        const prev = base.subtract(1, 'month');
                                        const start = prev.startOf('month').format('YYYY-MM-DD');
                                        const end = prev.endOf('month').format('YYYY-MM-DD');
                                        setDisplayDateStart(start);
                                        setDisplayDateEnd(end);
                                        setPage(1);
                                        fetchData(start, end);
                                    }}
                                >
                                    前月
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    sx={{ flex: 1, fontSize: '0.75rem', py: 0.5 }}
                                    onClick={() => {
                                        const start = dayjs().startOf('month').format('YYYY-MM-DD');
                                        const end = dayjs().endOf('month').format('YYYY-MM-DD');
                                        setDisplayDateStart(start);
                                        setDisplayDateEnd(end);
                                        setPage(1);
                                        fetchData(start, end);
                                    }}
                                >
                                    今月
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    sx={{ flex: 1, fontSize: '0.75rem', py: 0.5 }}
                                    onClick={() => {
                                        const base = displayDateStart ? dayjs(displayDateStart) : dayjs();
                                        const next = base.add(1, 'month');
                                        const start = next.startOf('month').format('YYYY-MM-DD');
                                        const end = next.endOf('month').format('YYYY-MM-DD');
                                        setDisplayDateStart(start);
                                        setDisplayDateEnd(end);
                                        setPage(1);
                                        fetchData(start, end);
                                    }}
                                >
                                    翌月
                                </Button>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setDisplayDateStart('');
                                        setDisplayDateEnd('');
                                        setPage(1);
                                        setDateRangeAnchor(null);
                                    }}
                                    fullWidth
                                    size="small"
                                    sx={{ color: 'text.secondary', borderColor: 'divider' }}
                                >
                                    指定解除
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        if (displayDateStart && displayDateEnd) {
                                            fetchData(displayDateStart, displayDateEnd);
                                        }
                                        setPage(1);
                                        setDateRangeAnchor(null);
                                    }}
                                    fullWidth
                                    size="small"
                                >
                                    適用
                                </Button>
                            </Box>
                            <Button
                                variant="outlined"
                                color="success"
                                onClick={handleDownloadCSV}
                                fullWidth
                                size="small"
                                startIcon={<DownloadIcon />}
                                sx={{ mt: 0.5 }}
                            >
                                CSV DL
                            </Button>
                        </Box>
                    </Popover>

                    <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        startIcon={<RefreshIcon />}
                        onClick={(e) => setRefreshMenuAnchor(e.currentTarget)}
                        disableElevation
                        sx={{ ml: 0.5, backgroundColor: '#334155', '&:hover': { backgroundColor: '#1e293b' } }}
                    >
                        データ更新
                    </Button>
                    <Menu
                        anchorEl={refreshMenuAnchor}
                        open={Boolean(refreshMenuAnchor)}
                        onClose={() => setRefreshMenuAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <MenuItem onClick={handleRefreshFuture}>
                            <Box sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>将来データを取得</Typography>
                                <Typography variant="caption" color="text.secondary">3日前 〜 1年後</Typography>
                            </Box>
                        </MenuItem>
                        <MenuItem onClick={handleRefreshPeriod} disabled={!displayDateStart || !displayDateEnd}>
                            <Box sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>期間指定データを取得</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {displayDateStart && displayDateEnd
                                        ? `${dayjs(displayDateStart).format('YY/MM/DD')} 〜 ${dayjs(displayDateEnd).format('YY/MM/DD')}`
                                        : '期間未指定'}
                                </Typography>
                            </Box>
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>

            {/* 統合フィルタパネル */}
            <Paper
                elevation={0}
                variant="outlined"
                sx={{
                    p: 2.5,
                    mb: 3,
                    backgroundColor: '#f8fafc',
                    borderColor: '#e2e8f0',
                    borderRadius: 2
                }}
            >
                <Grid container spacing={3}>
                    {/* ステータス */}
                    <Grid {...{item: true, xs: 12, sm: 6, md: 3} as any}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.8, display: 'block', ml: 0.2 }}>
                            ステータス
                        </Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                multiple
                                value={filterStatus}
                                displayEmpty
                                onChange={(e) => {
                                    const val = e.target.value;
                                    const arrayVal = typeof val === 'string' ? val.split(',') : val;
                                    // 値を小文字に統一して保持
                                    setFilterStatus(arrayVal.map(v => String(v).toLowerCase()));
                                }}
                                renderValue={(selected) => {
                                    if (selected.length === 0) return <Typography variant="body2" color="text.disabled">全て</Typography>;
                                    return selected.map(s => getStatusLabel(s)).join(', ');
                                }}
                                sx={{ backgroundColor: 'white' }}
                            >
                                {reservationStatusList.map((opt) => {
                                    const val = String(opt.code || opt.id).toLowerCase();
                                    return (
                                        <MenuItem key={val} value={val}>
                                            <Checkbox checked={filterStatus.indexOf(val) > -1} size="small" />
                                            <ListItemText primary={opt.label} />
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* 車種 */}
                    <Grid {...{item: true, xs: 12, sm: 6, md: 3} as any}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.8, display: 'block', ml: 0.2 }}>
                            車種
                        </Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                multiple
                                value={filterCarType}
                                displayEmpty
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFilterCarType(typeof val === 'string' ? val.split(',') : val);
                                }}
                                renderValue={(selected) => {
                                    if (selected.length === 0) return <Typography variant="body2" color="text.disabled">全て</Typography>;
                                    return selected.join(', ');
                                }}
                                sx={{ backgroundColor: 'white' }}
                            >
                                {carTypesList.map((ct) => (
                                    <MenuItem key={ct.id} value={ct.name}>
                                        <Checkbox checked={filterCarType.indexOf(ct.name) > -1} size="small" />
                                        <ListItemText primary={ct.name} secondary={ct.code} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* 車両 */}
                    <Grid {...{item: true, xs: 12, sm: 12, md: 3.5} as any}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.8, display: 'block', ml: 0.2 }}>
                            車両
                        </Typography>
                        <Autocomplete
                            multiple
                            options={vehicleList}
                            getOptionLabel={(option) => option.vehicle_code || option.name || String(option.id)}
                            value={filterVehicle}
                            onChange={(event, newValue) => setFilterVehicle(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="車両(複数可)"
                                    size="small"
                                    sx={{ backgroundColor: 'white' }}
                                />
                            )}
                            fullWidth
                        />
                    </Grid>

                    {/* 検索 & リセット */}
                    <Grid {...{item: true, xs: 12, md: 2.5} as any}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.8, display: 'block', ml: 0.2 }}>
                            フリーワード検索
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="顧客名、メアド、プラン等..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                sx={{ backgroundColor: 'white' }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchQuery && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={handleClearSearch}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Tooltip title="リセット">
                                <IconButton
                                    onClick={handleResetFilters}
                                    size="small"
                                    sx={{
                                        backgroundColor: '#f1f5f9',
                                        '&:hover': { backgroundColor: '#e2e8f0' },
                                        width: 38,
                                        height: 38,
                                        flexShrink: 0
                                    }}
                                >
                                    <RestartAltIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Grid>

                    {/* その他フィルタ */}
                    <Grid {...{item: true, xs: 12} as any}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                その他絞り込み:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <Chip
                                    label="車両未割当"
                                    onClick={handleFilterNoVehicleToggle}
                                    color={filterNoVehicle ? "primary" : "default"}
                                    variant={filterNoVehicle ? "filled" : "outlined"}
                                    size="medium"
                                    sx={{ height: 32, px: 1 }}
                                />
                                <Chip
                                    label="請求書未発行"
                                    onClick={handleFilterInvoiceToggle}
                                    color={filterInvoice ? "primary" : "default"}
                                    variant={filterInvoice ? "filled" : "outlined"}
                                    size="medium"
                                    sx={{ height: 32, px: 1 }}
                                />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* テーブル */}
            <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <SortableHeaderCell id="id" label="ID" width={60} />
                            <SortableHeaderCell id="contact_code" label="予約コード" width={120} />
                            <SortableHeaderCell id="status" label="ステータス" width={120} />
                            <SortableHeaderCell id="booking_date" label="予約日" width={140} />
                            <SortableHeaderCell id="customer_name" label="顧客名" width={150} />
                            <SortableHeaderCell id="vehicle_name" label="車両" width={100} />
                            <SortableHeaderCell id="car_type" label="車種" width={120} />
                            <SortableHeaderCell id="start_date" label="利用期間" width={220} />
                            <SortableHeaderCell id="contact_media" label="予約経路" width={100} />
                            <SortableHeaderCell id="sum_price" label="合計金額" width={100} align="right" />
                            <SortableHeaderCell id="payment" label="支払" width={80} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedReservations.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={11} sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        データがありません
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedReservations.map((res) => {
                                const statusColors = getStatusColor(res);
                                const carType = res.car_type || res.re_car_type || res.req_car_type || '-';
                                const vehicleId = res.vehicle_id ?? res.vehicleId;
                                const vehicleCode = vehicleId != null ? vehicleIdToCode[String(vehicleId)] : null;
                                return (
                                    <TableRow key={res.id} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        color: 'primary.main',
                                                        '&:hover': { textDecoration: 'underline' },
                                                    }}
                                                    onClick={() => setSelectedReservation(res)}
                                                >
                                                    {res.id}
                                                </Typography>
                                                <Tooltip title="コピー" arrow>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCopyReservation(res);
                                                        }}
                                                        sx={{ p: 0, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                                                    >
                                                        <ContentCopyIcon sx={{ fontSize: '0.9rem' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                {res.contact_code || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getStatusLabel(res.status)}
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setResStatusModal({ open: true, reservation: res });
                                                }}
                                                clickable
                                                sx={{
                                                    backgroundColor: statusColors.bg,
                                                    color: statusColors.text,
                                                    fontWeight: 500,
                                                    fontSize: '0.75rem',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>{formatDate(res.booking_date)}</TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    cursor: 'pointer',
                                                    color: 'primary.main',
                                                    '&:hover': { textDecoration: 'underline' },
                                                }}
                                                onClick={() => setSelectedReservation(res)}
                                            >
                                                {res.customer_name || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.8rem',
                                                    color: !selectedReservation ? (vehicleCode ? 'primary.main' : 'error.main') : 'text.disabled',
                                                    cursor: !selectedReservation ? 'pointer' : 'default',
                                                    '&:hover': !selectedReservation ? { textDecoration: 'underline' } : {},
                                                }}
                                                onClick={() => !selectedReservation && handleOpenAssignModal(res)}
                                            >
                                                {vehicleCode || '未割当'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    display: 'inline-flex',
                                                    flexDirection: 'column',
                                                    backgroundColor: resolveCarTypeColor(carType),
                                                    color: resolveCarTypeTextColor(carType),
                                                    px: 1,
                                                    py: 0.5,
                                                    borderRadius: 1,
                                                    minWidth: '80px',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem', lineHeight: 1.2 }}>
                                                    {carTypesList.find(c => c.code === carType || c.name === carType)?.name || carType}
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.9, lineHeight: 1.2 }}>
                                                    {carType}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{formatDateRange(res.start_date, res.end_date)}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                                {String(res.contact_media || res.detail?.contact_media || '-')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">{formatPrice(res.sum_price)}</TableCell>
                                        <TableCell>
                                            {(() => {
                                                const contactId = res.contact_code || res.detail?.contact_id;
                                                const paymentEntry = squarePaymentCache[contactId];
                                                const isManualPaid = Number(res.detail?.payment) === 1;
                                                const ps = computePaymentStatus(paymentEntry, isManualPaid, squarePaymentLoading);

                                                return (
                                                    <Box
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handlePaymentCheck(res);
                                                        }}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            display: 'inline-flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            gap: '2px',
                                                            padding: '4px',
                                                            borderRadius: '4px',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(0,0,0,0.04)',
                                                            },
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'inline-block',
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 'bold',
                                                                backgroundColor: ps.color + '15',
                                                                color: ps.color,
                                                                border: `1px solid ${ps.color}40`,
                                                                minWidth: '40px',
                                                                textAlign: 'center',
                                                            }}
                                                        >
                                                            {ps.text || '-'}
                                                        </Box>
                                                        {ps.hasRefunds && (
                                                            <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 'bold', fontSize: '0.6rem', lineHeight: 1 }}>
                                                                返金有
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                );
                                            })()}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* ページネーション */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {/* 予約詳細パネル */}
            <ReservationEditorPanel
                open={Boolean(selectedReservation)}
                editing={editingReservation}
                reservationStatusList={reservationStatusList}
                vehicles={vehicles}
                vehicleTypes={carTypesList}
                onCancel={() => setSelectedReservation(null)}
                onChange={(newFields) => setEditingReservation(prev => ({ ...prev, ...newFields }))}
                onAssignClick={() => handleOpenAssignModal(editingReservation)}
                registerNameInputRef={() => {}}
                onSave={async (_data: any, opts: any = {}) => {
                    const { keepOpen = false } = opts;
                    if (editingReservation) {
                        try {
                            const saved = await saveDispatchReservation(editingReservation);

                            // 1. ローカルの状態を即座に更新 (Enrichment付き)
                            const updatedWithCode = { ...saved };
                            if (updatedWithCode.vehicle_id && vehicles[String(updatedWithCode.vehicle_id)]) {
                                updatedWithCode.vehicle_code = vehicles[String(updatedWithCode.vehicle_id)].vehicle_code;
                            }
                            setReservations(prev => {
                                const map = new Map((Array.isArray(prev) ? prev : []).map(r => [String(r.id), r]));
                                map.set(String(updatedWithCode.id), updatedWithCode);
                                return Array.from(map.values());
                            });

                            // 2. バックグラウンドで期間データを再取得（サイレントリフレッシュ）
                            const start = displayDateStart || dayjs().subtract(3, 'days').format('YYYY-MM-DD');
                            const end = displayDateEnd || dayjs().add(1, 'year').format('YYYY-MM-DD');
                            fetchData(start, end, true);

                            if (!keepOpen) {
                                setSelectedReservation(null);
                            } else {
                                // 編集中のオブジェクトを更新（idがついたりする可能性があるので）
                                setSelectedReservation(saved);
                            }
                            setSnackbarMessage('予約を保存しました');
                            setSnackbarSeverity('success');
                            setSnackbarOpen(true);
                        } catch (err) {
                            console.error('Failed to save reservation:', err);
                            setSnackbarMessage('保存に失敗しました');
                            setSnackbarSeverity('error');
                            setSnackbarOpen(true);
                        }
                    }
                }}
                disabled={false}
            />

            {/* 予約ステータス変更ダイアログ */}
            <Dialog open={resStatusModal.open} onClose={() => setResStatusModal({ open: false, reservation: null })}>
                <DialogTitle>予約ステータス変更</DialogTitle>
                <DialogContent>
                    <Grid container spacing={1} sx={{ mt: 1 }}>
                        {reservationStatusList.map((status) => (
                            <Grid {...{item: true, xs: 6} as any} key={status.id}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={() => handleResStatusUpdate(status.id)}
                                    sx={{
                                        py: 1.5,
                                        backgroundColor: getStatusColor(status.id).bg || '#eee',
                                        color: getStatusColor(status.id).text || '#000',
                                        '&:hover': {
                                            backgroundColor: getStatusColor(status.id).bg || '#eee',
                                            filter: 'brightness(0.9)',
                                        }
                                    }}
                                >
                                    {status.label}
                                </Button>
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResStatusModal({ open: false, reservation: null })}>キャンセル</Button>
                </DialogActions>
            </Dialog>

            {/* 車両アサインモーダル */}
            <VehicleAssignModal
                open={assignModalOpen}
                onClose={handleCloseAssignModal}
                reservation={assignTargetReservation}
                vehicleList={vehicleList}
                vehicles={vehicles}
                carTypesList={carTypesList}
                onAssign={executeAssignment}
                onUpdateVehicleStatus={handleUpdateVehicleStatus}
                vehicleStatusList={vehicleStatusList}
                allReservations={reservations}
            />


            {/* 重複予約リストモーダル */}
            <Dialog
                open={duplicatesModalOpen}
                onClose={() => setDuplicatesModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>重複予約一覧 ({duplicateReservations.length}件)</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        以下の予約は、同一車両・同一期間で重複しています。クリックして詳細を確認・修正してください。
                    </DialogContentText>
                    <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>ID</TableCell>
                                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>車両</TableCell>
                                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>顧客名</TableCell>
                                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>期間</TableCell>
                                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>ステータス</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {duplicateReservations.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">重複予約はありません</TableCell>
                                    </TableRow>
                                ) : (
                                    duplicateReservations.map(res => {
                                        const vehicleId = res.vehicle_id ?? res.vehicleId;
                                        const vehicleCode = vehicleId != null ? vehicleIdToCode[String(vehicleId)] : '-';
                                        const statusColors = getStatusColor(res);

                                        return (
                                            <TableRow
                                                key={res.id}
                                                hover
                                                onClick={() => {
                                                    setDuplicatesModalOpen(false);
                                                    setSelectedReservation(res);
                                                }}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                <TableCell>{res.id}</TableCell>
                                                <TableCell>{vehicleCode}</TableCell>
                                                <TableCell>{res.customer_name}</TableCell>
                                                <TableCell>{formatDateRange(res.start_date, res.end_date)}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={getStatusLabel(res.status)}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: statusColors.bg,
                                                            color: statusColors.text,
                                                            fontSize: '0.75rem',
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDuplicatesModalOpen(false)}>閉じる</Button>
                </DialogActions>
            </Dialog>
            {/* Square 支払確認モーダル */}
            <Dialog
                open={squareModal.open}
                onClose={() => setSquareModal({ open: false, reservation: null, paymentData: null, loading: false })}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="h6" component="span">支払確認</Typography>
                        {squareModal.reservation && (
                            <Typography variant="subtitle2" color="text.secondary">
                                {squareModal.reservation.customer_name} ({squareModal.reservation.contact_code || squareModal.reservation.detail?.contact_id})
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* 支払状態の手動変更ボタン */}
                        {(() => {
                            const currentPayment = Number(squareModal.reservation?.detail?.payment ?? 0);
                            const isPaid = currentPayment === 1;

                            return (
                                <Button
                                    variant={isPaid ? 'outlined' : 'contained'}
                                    color={isPaid ? 'warning' : 'success'}
                                    size="small"
                                    onClick={async () => {
                                        const reservation = squareModal.reservation;
                                        if (!reservation) return;

                                        const newPaymentStatus = isPaid ? 0 : 1;

                                        try {
                                            await saveDispatchReservation({
                                                ...reservation,
                                                detail: {
                                                    // NOTE: Fallback required for spread. Detail is validated non-null by SyncService, but kept for runtime safety.
                                                    ...(reservation.detail || {}),
                                                    payment: newPaymentStatus
                                                }
                                            });

                                            // ローカルステートを更新
                                            setReservations(prev => prev.map(r => {
                                                if (String(r.id) !== String(reservation.id)) return r;
                                                return {
                                                    ...r,
                                                    // NOTE: Fallback required for spread - detail may be null/undefined
                                                    detail: { ...(r.detail || {}), payment: newPaymentStatus }
                                                };
                                            }));

                                            // モーダルの表示用予約オブジェクトも更新
                                            setSquareModal(prev => ({
                                                ...prev,
                                                reservation: {
                                                    ...prev.reservation,
                                                    detail: {
                                                        // NOTE: Fallback required for spread - detail may be null/undefined
                                                        ...(prev.reservation.detail || {}),
                                                        payment: newPaymentStatus
                                                    }
                                                }
                                            }));

                                            setSnackbarMessage(newPaymentStatus === 1 ? '支払確認済みに更新しました' : '未払いに戻しました');
                                            setSnackbarSeverity('success');
                                            setSnackbarOpen(true);
                                        } catch (error) {
                                            console.error('Failed to update payment status:', error);
                                            setSnackbarMessage('更新に失敗しました');
                                            setSnackbarSeverity('error');
                                            setSnackbarOpen(true);
                                        }
                                    }}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    {isPaid ? '未払に戻す' : '確認済みにする'}
                                </Button>
                            );
                        })()}
                        <IconButton
                            onClick={() => setSquareModal({ open: false, reservation: null, paymentData: null, loading: false })}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ minHeight: '150px' }}>
                    {squareModal.loading ? (
                        <Box sx={{ py: 6, textAlign: 'center' }}>
                            <CircularProgress size={30} sx={{ mb: 2 }} />
                            <Typography color="text.secondary">Squareデータを読み込み中...</Typography>
                        </Box>
                    ) : !squareModal.paymentData || (squareModal.paymentData.invoices?.length ?? 0) === 0 ? (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                該当するSquare請求データがありません
                            </Typography>
                        </Box>
                    ) : (
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#7c3aed' }}>
                                Square決済DB ({squareModal.paymentData.invoices?.length || 0}件)
                            </Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#f3e8ff' }}>
                                            <TableCell sx={{ fontWeight: 600 }}>請求番号</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>ステータス</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>金額</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>支払日</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>作成日</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>商品</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {squareModal.paymentData.invoices.map((inv: any, idx: number) => {
                                            const order = squareModal.paymentData.orders?.find((o: any) => o.order_id === inv.order_id);
                                            const lineItems = order?.line_items || [];
                                            const payment = squareModal.paymentData.payments?.find((p: any) => p.order_id === inv.order_id);
                                            const statusColor = { PAID: '#10b981', COMPLETED: '#10b981', CANCELED: '#ef4444', UNPAID: '#f59e0b', PARTIALLY_PAID: '#3b82f6' }[inv.status] || '#94a3b8';
                                            return (
                                                <TableRow key={idx}>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                        {inv.invoice_number || '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip label={inv.status} size="small" sx={{ bgcolor: statusColor, color: '#fff', fontSize: '0.7rem', fontWeight: 600 }} />
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>
                                                        ¥{((inv.computed_amount || 0) / 1).toLocaleString()}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.8rem' }}>
                                                        {payment?.created_at ? dayjs(payment.created_at).format('MM/DD HH:mm') : inv.updated_at ? dayjs(inv.updated_at).format('MM/DD HH:mm') : '-'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.8rem' }}>
                                                        {inv.created_at ? dayjs(inv.created_at).format('YYYY/MM/DD') : '-'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem', maxWidth: 200 }}>
                                                        {lineItems.length > 0
                                                            ? lineItems.map((li: any, i: number) => (
                                                                <div key={i}>{li.name} x{li.quantity}</div>
                                                            ))
                                                            : '-'}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {squareModal.paymentData.refunds?.length > 0 && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    返金: {squareModal.paymentData.refunds.length}件
                                    (¥{squareModal.paymentData.refunds.reduce((sum: number, r: any) => sum + Math.round((Number(r.amount) || 0) / 100), 0).toLocaleString()})
                                </Alert>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSquareModal({ open: false, reservation: null, paymentData: null, loading: false })}>閉じる</Button>
                </DialogActions>
            </Dialog>
            {/* スナックバー */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity as any} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}
